import { useEffect, useState } from "react";
import PageLayout from "../common/PageLayout";
import { useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { TbListDetails } from "react-icons/tb";
import { AppEntryRepository } from "../repositories/AppEntryRepository";
import EntryDetails from "./EntryDetails";
import EntryAttachedDocuments from "./EntryAttachedDocuments";
import EntryOtherDocuments from "./EntryOtherDocuments";
import EntryDecisions from "./EntryDecisions";
import { FiPlusCircle } from "react-icons/fi";
import { MdPayment } from "react-icons/md";
import { MdCancel } from "react-icons/md";
import { MdSend } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdEditNote } from "react-icons/md";
import { MdAssignmentTurnedIn } from "react-icons/md";
import { MdNotes } from "react-icons/md";
import { MdVisibility } from "react-icons/md";
import { MdGavel } from "react-icons/md";
import { MdDraw } from "react-icons/md";
import { MdCheckCircle } from "react-icons/md";
import { MdCancelPresentation } from "react-icons/md";
import EntryStatusesPanel from "./EntryStatusesPanel";
import EntryRequestsStepper from "./EntryRequestsStepper";
import EntryCorrectedAttachments from "./EntryCorrectedAttachments";
import EntryGeneratedRequests from "./EntryGeneratedRequests";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { FinalDecisionForm } from "./FinalDecisionForm";
import EntryFinalDocuments from "./EntryFinalDocuments";
import ConfirmationDialog from "../common/ConfirmationDialog";

const statusIcons = {
  NEW: <FiPlusCircle />,
  PENDING_PAYMENT: <MdPayment />,
  SUBMITTED: <MdSend />,
  REJECTED: <MdCancel />,
  PROCESSING: <AiOutlineLoading3Quarters />,
  CORRECTION: <MdEditNote />,
  FOR_APPROVAL: <MdAssignmentTurnedIn />,
  ADDITIONAL_CORRECTION: <MdNotes />,
  INSTITUTIONS_REVIEW: <MdVisibility />,
  INSTITUTION_DECISION: <MdGavel />,
  FINAL_DECISION_SIGNING: <MdDraw />,
  DONE: <MdCheckCircle />,
  CANCELED: <MdCancelPresentation />,
};

export default function EntryDetailedView() {
  const theme = useTheme();
  const { id } = useParams();
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [appEntry, setAppEntry] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [archiveNumber, setArchiveNumber] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      getById();
    }
  }, [id]);

  const getById = () => {
    setLoading(true);
    AppEntryRepository.getById(id)
      .then((res) => {
        setLoading(false);
        setAppEntry(res.data);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  const attachedDocuments = appEntry?.attachments?.filter((attachment) =>
    [
      "URBAN_PLANING_PROJECT_DWG",
      "URBAN_PLANING_PROJECT_PDF",
      "TRAFFIC_ENGINEERING_PROJECT_DWG",
      "TRAFFIC_ENGINEERING_PROJECT_PDF",
      "PROJECT_REVISION",
    ].includes(attachment.documentType),
  );

  const otherDocuments = appEntry?.attachments?.filter((attachment) =>
    ["PROOF_OF_PAYMENT", "POWER_OF_ATTORNEY"].includes(attachment.documentType),
  );
  const correctedDocuments = appEntry?.attachments?.filter((attachment) =>
    ["CORRECTED"].includes(attachment.documentType),
  );

  const assignArchiveNumber = () => {
    setSaving(true);
    setLoadingStatus(true);
    AppEntryRepository.assignArchiveNumber(appEntry?.id, archiveNumber)
      .then((res) => {
        setLoadingStatus(false);
        setAppEntry(res.data);
        dispatch(
          notifyShowSuccessMessage(
            "Успешно е доделен архивски број на предметот. Предметот продолжува во следната фаза на обработка.",
          ),
        );
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      })
      .finally(() => {
        setLoadingStatus(false);
        setSaving(false);
      });
  };

  return (
    <PageLayout
      title={appEntry?.trackingNumber}
      Icon={TbListDetails}
      subtitle={"Идентификатор"}
    >
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Grid container spacing={0}>
          <Grid item xs={12} md={12} sx={{ mb: 2 }}>
            <EntryStatusesPanel appEntry={appEntry} setAppEntry={setAppEntry} />
          </Grid>

          {appEntry?.status == "INSTITUTION_DECISION" &&
            auth?.userData?.roles?.includes("ROLE_MT_SUPERVISOR") && (
              <Grid item xs={12} md={12}>
                <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
                  <Typography variant="h6" component="div">
                    Изработка на конечна одлука
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} style={{ marginTop: "10px" }}>
                      <FinalDecisionForm
                        appEntry={appEntry}
                        handleReloadAppEntry={() => getById()}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

          {appEntry?.status == "FOR_APPROVAL" && (
            <Grid item xs={12} md={12}>
              <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
                <Typography variant="h6" component="div">
                  Генерирање на барања за согласност до институции
                </Typography>
                <EntryRequestsStepper
                  appEntry={appEntry}
                  setAppEntry={setAppEntry}
                  isGenerating={isGenerating}
                  onStartGenerating={() => setIsGenerating(true)}
                  onCancelGenerating={() => setIsGenerating(false)}
                />
              </Paper>
            </Grid>
          )}

          {appEntry?.status === "DONE" && (
            <EntryFinalDocuments appEntry={appEntry} />
          )}

          {appEntry?.status === "SUBMITTED" &&
            auth?.userData?.roles?.includes("ROLE_MT_ARCHIVER") && (
              <Grid item xs={12} md={12}>
                <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
                  <Typography variant="h6" component="div">
                    Архивирање на предмет
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <TextField
                      size="small"
                      label="Архивски број"
                      autoComplete="off"
                      value={archiveNumber || ""}
                      onChange={(e) => setArchiveNumber(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={!archiveNumber}
                      onClick={() =>
                        setOpenConfirmationDialog({
                          title: "Потврда за архивирање",
                          message: `Дали сте сигурни дека сакате да го архивирате предметот и да му доделите архивски број ${archiveNumber}?`,
                          onConfirm: () => {
                            assignArchiveNumber();
                            setOpenConfirmationDialog(null);
                          },
                        })
                      }
                      sx={{
                        px: 3,
                        py: 1,
                        "&.Mui-disabled": {
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          cursor: "not-allowed",
                          pointerEvents: "auto",
                          opacity: 1,
                        },
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Inter-Regular",
                          fontSize: "14px",
                          color: "#fff",
                        }}
                      >
                        Зачувај
                      </Typography>
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

          {!isGenerating && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
                <EntryDetails appEntry={appEntry} />
                <EntryAttachedDocuments
                  appEntry={appEntry}
                  attachedDocuments={attachedDocuments}
                />
              </Grid>
              <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
                <EntryOtherDocuments
                  otherDocuments={otherDocuments}
                  appEntry={appEntry}
                />
                {correctedDocuments?.length > 0 && (
                  <EntryCorrectedAttachments
                    correctedDocuments={correctedDocuments}
                    appEntry={appEntry}
                  />
                )}
                <EntryDecisions appEntry={appEntry} />
                <EntryGeneratedRequests appEntry={appEntry} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 1000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
    </PageLayout>
  );
}
